import moment from "moment";
import i18n from "../../../../services/i18n";
import {InstallmentChangeType} from "../../../../generated/graphql";

/**
 * Übersetzt eine Korrektur-Vorschau in ganze Sätze („Das passiert beim Ausführen").
 *
 * Warum nicht die rohen `installmentChanges` anzeigen: Zeilen wie
 * „Rate durch Teilzahlung beglichen — Restbetrag entfällt: CHF 280 → CHF 230" beschreiben
 * den Datensatz, nicht den Vorgang. Dieselbe Liste trägt Schritt 2 (Futur) und Schritt 3
 * (Perfekt), damit das Ergebnis exakt das bestätigt, was vorher angekündigt wurde.
 */

export type SentenceChange = {
    type: InstallmentChangeType;
    spId?: string | null;
    title?: string | null;
    oldAmount?: number | null;
    newAmount?: number | null;
    paymentDue?: Date | string | null;
};

export type SentencePreview = {
    newGrossPrice: number;
    refundAmount: number;
    creditToBooker: number;
    newPaymentLinkAmount: number;
    installmentChanges: ReadonlyArray<SentenceChange>;
};

/** Rate der Buchung — liefert das Fälligkeitsdatum für die Satz-Referenz. */
export type SentenceRate = {
    id: string;
    paymentDue?: Date | string | null;
};

export type CorrectionSentence = {
    /** Stabiler Schlüssel für React und für Tests (Textänderungen brechen die Tests nicht). */
    key: string;
    text: string;
    hint?: string;
};

/**
 * Ergebnis der Ausführung. Die Schritte nach dem Commit (Erstattung, Zahlungslink,
 * Korrekturrechnung) können einzeln fehlschlagen, ohne die Korrektur zu kippen — der Executor
 * meldet das über diese Flags und eine Meldung. Ein Häkchen für einen gescheiterten Schritt
 * würde der Meldung widersprechen und zum Überspringen des nötigen Nachholens verleiten.
 */
export type SentenceOutcome = {
    refundOk: boolean;
    paymentLinkOk: boolean;
    correctionInvoiceOk: boolean;
};

type BuildParams = {
    preview: SentencePreview;
    oldGrossPrice: number;
    /** false = Vorschau (Futur), true = Ergebnis (Perfekt). */
    done: boolean;
    /** Nur im Ergebnis-Schritt: unterdrückt die Sätze fehlgeschlagener Folgeschritte. */
    outcome?: SentenceOutcome;
    /** Raten der Buchung, für die Datums-Referenz der geänderten Rate. */
    rates: ReadonlyArray<SentenceRate>;
    reason: string;
    renderMoney: (value: number | null | undefined) => string;
};

const formatDate = (value: Date | string | null | undefined): string | null => {
    if (!value) {
        return null;
    }
    const parsed = moment(value);

    return parsed.isValid() ? parsed.format("DD.MM.YYYY") : null;
};

/**
 * Bezeichnet eine Rate im Satz: „vom 19.09.2026", sonst der Titel, sonst die Position.
 * Immer ein vollständiger Einschub, damit der Satz in jedem Fall grammatikalisch aufgeht.
 */
const rateRef = (change: SentenceChange, rates: ReadonlyArray<SentenceRate>, index: number): string => {
    const tr = i18n.containers.invoice.CorrectBookingWizard.rateRef;
    const rate = change.spId ? rates.filter((r) => r.id === change.spId)[0] : undefined;
    const date = formatDate(rate?.paymentDue);

    if (date) {
        return tr.byDate({date});
    }
    if (change.title) {
        return tr.byTitle({title: change.title});
    }

    return tr.byIndex({index: String(index + 1)});
};

export const buildCorrectionSentences = ({
    preview,
    oldGrossPrice,
    done,
    outcome,
    rates,
    reason,
    renderMoney,
}: BuildParams): Array<CorrectionSentence> => {
    const tr = i18n.containers.invoice.CorrectBookingWizard.sentences;
    const sentences: Array<CorrectionSentence> = [];
    // Im Ergebnis nur bestätigen, was tatsächlich durchgelaufen ist.
    const confirmed = (ok: boolean | undefined): boolean => !done || ok !== false;
    const newGross = Number(preview.newGrossPrice);
    const oldMoney = renderMoney(oldGrossPrice);
    const newMoney = renderMoney(newGross);

    if (newGross !== Number(oldGrossPrice)) {
        sentences.push({
            key: "price",
            text: done
                ? tr.priceDone({old: oldMoney, new: newMoney})
                : newGross < Number(oldGrossPrice)
                    ? tr.priceLowered({old: oldMoney, new: newMoney})
                    : tr.priceRaised({old: oldMoney, new: newMoney}),
        });
    }

    preview.installmentChanges.forEach((change, index) => {
        const ref = rateRef(change, rates, index);
        const oldAmount = renderMoney(change.oldAmount);
        const newAmount = renderMoney(change.newAmount);

        switch (change.type) {
            case InstallmentChangeType.Settle:
                // SETTLE: die Teilzahlung (newAmount) schließt die Rate ab, der Rest entfällt.
                sentences.push({
                    key: `settle-${index}`,
                    text: done
                        ? tr.settleDone({rate: ref})
                        : tr.settle({
                            rate: ref,
                            paid: newAmount,
                            dropped: renderMoney(Number(change.oldAmount ?? 0) - Number(change.newAmount ?? 0)),
                        }),
                });
                break;
            case InstallmentChangeType.Adjust:
                sentences.push({
                    key: `adjust-${index}`,
                    text: done ? tr.adjustDone({rate: ref, new: newAmount}) : tr.adjust({rate: ref, old: oldAmount, new: newAmount}),
                });
                break;
            case InstallmentChangeType.Remove:
                sentences.push({
                    key: `remove-${index}`,
                    text: done ? tr.removeDone({rate: ref}) : tr.remove({rate: ref}),
                });
                break;
            case InstallmentChangeType.Create: {
                const date = formatDate(change.paymentDue) ?? "";

                sentences.push({
                    key: `create-${index}`,
                    text: done ? tr.createDone({new: newAmount, date}) : tr.create({new: newAmount, date}),
                });
                break;
            }
            case InstallmentChangeType.VoidLink:
                sentences.push({key: `void-${index}`, text: done ? tr.voidLinkDone() : tr.voidLink()});
                break;
            case InstallmentChangeType.Refund:
                if (confirmed(outcome?.refundOk)) {
                    sentences.push({
                        key: `refund-${index}`,
                        text: done ? tr.refundDone({amount: newAmount}) : tr.refund({amount: newAmount}),
                    });
                }
                break;
            case InstallmentChangeType.NewLink:
                // Der Betrag steht als newPaymentLinkAmount in der Vorschau; der Step selbst trägt ihn nicht.
                if (confirmed(outcome?.paymentLinkOk)) {
                    sentences.push({
                        key: `link-${index}`,
                        text: done
                            ? tr.newLinkDone({amount: renderMoney(preview.newPaymentLinkAmount)})
                            : tr.newLink({amount: renderMoney(preview.newPaymentLinkAmount)}),
                    });
                }
                break;
            default:
                break;
        }
    });

    if (Number(preview.creditToBooker) > 0) {
        const amount = renderMoney(preview.creditToBooker);

        sentences.push({
            key: "credit",
            text: done ? tr.creditToBookerDone({amount}) : tr.creditToBooker({amount}),
        });
    }

    // Die Korrekturrechnung entsteht bei jeder Ausführung (executor Schritt 6) — sie gehört
    // deshalb bedingungslos in die Ankündigung. Im Ergebnis nur, wenn sie wirklich entstand;
    // sonst erklärt die Meldung des Executors, wie sie nachzuholen ist.
    if (confirmed(outcome?.correctionInvoiceOk)) {
        sentences.push({
            key: "invoice",
            text: done ? tr.correctionInvoiceDone({amount: newMoney}) : tr.correctionInvoice({amount: newMoney}),
            hint: done ? undefined : tr.correctionInvoiceHint(),
        });
    }

    const trimmedReason = reason.trim();

    if (trimmedReason) {
        sentences.push({
            key: "reason",
            text: done ? tr.reasonDone({reason: trimmedReason}) : tr.reason({reason: trimmedReason}),
        });
    }

    return sentences;
};
